/**
 * Extract the domain from a URL
 * @param url - Full URL
 * @returns domain string (e.g., "example.com"), lowercase
 */
export function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.toLowerCase()
  } catch {
    return ''
  }
}

/**
 * Add www. prefix to a hostname if it's a bare domain (no subdomain)
 * @param hostname - Hostname string (e.g., "example.com" or "blog.example.com")
 * @returns Hostname with www. prefix if needed
 * @example
 * addWwwPrefix('example.com') // 'www.example.com'
 * addWwwPrefix('www.example.com') // 'www.example.com'
 * addWwwPrefix('blog.example.com') // 'blog.example.com' (subdomain, unchanged)
 * addWwwPrefix('example.co.uk') // 'www.example.co.uk'
 */
function addWwwPrefix(hostname: string): string {
  // Already has www
  if (hostname.startsWith('www.')) {
    return hostname
  }

  const parts = hostname.split('.')

  // For simple TLDs (example.com has 2 parts), add www
  if (parts.length === 2) {
    return `www.${hostname}`
  }

  // For 3-part domains, check if it's a known country-code TLD pattern
  // Common patterns: .co.uk, .com.au, .org.uk, .net.au, etc.
  if (parts.length === 3) {
    const secondLast = parts[parts.length - 2]
    const last = parts[parts.length - 1]
    const ccTldPatterns = ['co', 'com', 'org', 'net', 'gov', 'edu', 'ac']
    const countryTlds = ['uk', 'au', 'nz', 'za', 'in', 'jp', 'kr', 'br', 'mx']

    // If it matches a country-code TLD pattern, add www
    if (ccTldPatterns.includes(secondLast) && countryTlds.includes(last)) {
      return `www.${hostname}`
    }
  }

  // Otherwise, assume it's a subdomain and don't add www
  return hostname
}

/**
 * Normalize a domain by ensuring it has a protocol, www. prefix for bare domains,
 * and extracting just the origin (protocol + hostname), stripping any paths or trailing slashes
 * @param domain - Domain string (with or without protocol, possibly with path)
 * @returns Normalized domain origin with https:// protocol and www. prefix (no trailing slash)
 * @example
 * normalizeDomain('example.com') // 'https://www.example.com'
 * normalizeDomain('www.example.com') // 'https://www.example.com'
 * normalizeDomain('https://example.com') // 'https://www.example.com'
 * normalizeDomain('https://example.com/') // 'https://www.example.com'
 * normalizeDomain('https://example.com/path/to/page') // 'https://www.example.com'
 * normalizeDomain('blog.example.com') // 'https://blog.example.com' (subdomain unchanged)
 * normalizeDomain('http://example.com') // 'http://www.example.com'
 */
export function normalizeDomain(domain: string): string {
  if (!domain) return ''

  const trimmed = domain.trim()
  if (!trimmed) return ''

  try {
    // Ensure we have a protocol for URL parsing
    const urlString = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`
    const url = new URL(urlString)

    // Add www. prefix if needed
    const normalizedHostname = addWwwPrefix(url.hostname.toLowerCase())

    return `${url.protocol}//${normalizedHostname}`
  } catch {
    // Fallback: add protocol if not a valid URL
    return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`
  }
}

/**
 * Convert a domain string (which may or may not include protocol) to a
 * full URL safe for use in href attributes. Prevents double-protocol issues
 * when the stored domain already includes "https://".
 * @example
 * domainToUrl('example.com')                 // 'https://example.com'
 * domainToUrl('https://example.com')         // 'https://example.com'
 * domainToUrl('https://www.example.com/')    // 'https://www.example.com'
 * domainToUrl('http://example.com')          // 'http://example.com'
 */
export function domainToUrl(domain: string): string {
  if (!domain) return '#'
  const trimmed = domain.trim().replace(/\/+$/, '')
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed
  }
  return `https://${trimmed}`
}

// ── Integration / subdomain utilities ────────────────────────────────────────

/**
 * Strip protocol, path, and www. from a domain for matching purposes.
 * Different from `normalizeDomain` which adds protocol + www.
 * @example
 * normalizeDomainForMatching('https://www.example.com/page') // 'example.com'
 * normalizeDomainForMatching('blog.example.com') // 'blog.example.com'
 */
export function normalizeDomainForMatching(domain: string): string {
  return domain
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '')
}

/**
 * Extract root domain from a hostname (e.g., blog.helpfulhero.com → helpfulhero.com)
 * Handles compound TLDs like .co.uk, .com.au
 */
export function extractRootFromDomain(domain: string): string {
  const cleaned = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '')
  const parts = cleaned.split('.')
  const compoundTlds = [
    'co.uk', 'org.uk', 'ac.uk', 'gov.uk',
    'com.au', 'net.au', 'org.au',
    'co.nz', 'co.za', 'com.br', 'com.mx',
    'co.jp', 'co.kr', 'co.in', 'co.id', 'co.th',
    'co.de', 'co.fr', 'co.it', 'co.es', 'com.es',
  ]
  const lastTwo = parts.slice(-2).join('.')
  if (compoundTlds.includes(lastTwo) && parts.length >= 3) {
    return parts.slice(-3).join('.')
  }
  return parts.length >= 2 ? parts.slice(-2).join('.') : cleaned
}

// Import types inline to avoid circular deps — only used for grouping utility
import type { UnifiedDomain } from '@shared/types'

export interface DomainGroup {
  rootDomain: string
  root: UnifiedDomain | null
  subdomains: UnifiedDomain[]
}

/**
 * Group an array of domains by their root domain.
 * Root domains appear as `group.root`, subdomains in `group.subdomains`.
 * Maintains original domain order for roots, sorts subdomains alphabetically.
 */
export function groupDomainsByRoot(domains: UnifiedDomain[]): DomainGroup[] {
  const groupMap = new Map<string, { root: UnifiedDomain | null; subdomains: UnifiedDomain[] }>()
  const insertionOrder: string[] = []

  for (const domain of domains) {
    const rootKey = extractRootFromDomain(domain.domain)
    const isRoot = domain.domain.toLowerCase() === rootKey

    if (!groupMap.has(rootKey)) {
      groupMap.set(rootKey, { root: null, subdomains: [] })
      insertionOrder.push(rootKey)
    }
    const group = groupMap.get(rootKey)!

    if (isRoot) {
      group.root = domain
    } else {
      group.subdomains.push(domain)
    }
  }

  return insertionOrder.map(rootDomain => {
    const { root, subdomains } = groupMap.get(rootDomain)!
    subdomains.sort((a, b) => a.domain.localeCompare(b.domain))
    return { rootDomain, root, subdomains }
  })
}

/**
 * Find matching HubSpot connection by domain
 * @param connections - List of HubSpot connections
 * @param url - URL to match
 * @returns Matching connection or null
 */
export function findConnectionByDomain(
  connections: Array<{ id: string; associatedDomains?: string[]; isActive: boolean }>,
  url: string
): { id: string; associatedDomains?: string[]; isActive: boolean } | null {
  const domain = extractDomain(url)
  if (!domain) return null

  // Find connection that has this domain in its associatedDomains (case-insensitive match)
  return connections.find(conn =>
    conn.isActive &&
    conn.associatedDomains &&
    conn.associatedDomains.some(d => d.toLowerCase() === domain)
  ) || null
}

/**
 * Check if a URL belongs to a domain (exact match or subdomain).
 * @param url - Full URL or path (e.g., "https://blog.example.com/post")
 * @param domain - The domain to check against (e.g., "example.com")
 * @returns true if the URL's hostname matches or is a subdomain
 */
export function isDomainMatch(url: string, domain: string): boolean {
  try {
    const urlString = url.startsWith('http') ? url : `https://${url}`
    const urlHost = new URL(urlString).hostname.toLowerCase().replace(/^www\./, '')
    const targetDomain = domain.toLowerCase().replace(/^www\./, '')
    return urlHost === targetDomain || urlHost.endsWith('.' + targetDomain)
  } catch {
    return false
  }
}
